export function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (m) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[m]));
}

export function fmtSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

// Windows rejects < > : " / \ | ? * and control characters in filenames, and
// trims trailing dots/spaces. <a download> sanitizes on its own, but the File
// System Access API throws instead — which would abort a bulk save halfway —
// so both paths go through this and end up with identical names.
export function safeFileName(name) {
  return String(name)
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
    .replace(/[. ]+$/, '')
    || 'untitled';
}

// Save a Blob under `filename` via a throwaway <a download>. The object URL is
// kept alive for a minute: a bulk save queues many downloads at once, and if
// Chrome's "allow multiple downloads?" prompt is waiting on the user, revoking
// early kills every download still queued behind it — files silently missing.
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeFileName(filename);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ===== Saving with a user-chosen path =====
// Chromium exposes the File System Access API on secure origins (localhost
// counts), which lets the user pick where files land instead of dumping them
// in the browser's download folder. Firefox and Safari have neither picker, so
// every helper below falls back to the <a download> path.
export const canPickPath = () => typeof window.showSaveFilePicker === 'function';

// Ask where to save one file. Three outcomes the caller must tell apart: a
// handle, null when the user cancelled, and undefined when this browser has no
// picker (fall back to downloadBlob). The `id` makes Chrome reopen at the
// folder chosen last time.
//
// The picker consumes the click's transient user activation, which expires
// after a few seconds — so a caller that has to build a big blob first must
// still pick BEFORE building it, then write into the handle.
export async function pickSaveFile(filename) {
  if (!canPickPath()) return undefined;
  const name = safeFileName(filename);
  const ext = (name.match(/\.[^.]+$/) || ['.html'])[0].toLowerCase();
  const mime = ext === '.zip' ? 'application/zip' : 'text/html';
  try {
    return await window.showSaveFilePicker({
      id: 'pawOut',
      startIn: 'downloads',
      suggestedName: name,
      types: [{ description: ext.slice(1).toUpperCase(), accept: { [mime]: [ext] } }],
    });
  } catch (err) {
    if (err.name === 'AbortError') return null;
    return undefined;   // blocked by policy, sandboxed iframe — plain download
  }
}

export async function writeFile(handle, blob) {
  const w = await handle.createWritable();
  await w.write(blob);
  await w.close();
}

// Save one blob, letting the user choose the path and name. Returns false only
// when they cancelled the dialog.
export async function saveBlobAs(blob, filename) {
  const handle = await pickSaveFile(filename);
  if (handle === null) return false;
  if (handle === undefined) { downloadBlob(blob, filename); return true; }
  await writeFile(handle, blob);
  return true;
}


// Lightweight transient toast — bottom-center, auto-dismisses.
let toastTimer = null;
export function toast(message) {
  let el = document.getElementById('pawToast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'pawToast';
    el.className = 'paw-toast';
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('show'), 2200);
}

